import type { CookieConsentRegion } from '@repo/common';
import { classifyHttpStatus, SessionErrorKind } from '@repo/common';

import { initializeCookieConsent } from '../contexts/cookie-consent-engine';

/**
 * Response from the /api/initialize endpoint
 */
interface InitializeResponse {
  consentRegion?: CookieConsentRegion;
  csrfToken: string;
  isAuthenticated: boolean;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
}

export type AppInitResult =
  | { status: 'ok'; data: InitializeResponse }
  | { status: 'transient-failure' }
  | { status: 'terminal-failure' }
  | { status: 'unauthenticated'; data: InitializeResponse };

const initializationRequests = new Map<string, Promise<AppInitResult>>();

function readInitializeResponse(value: unknown): InitializeResponse | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const csrfToken = Reflect.get(value, 'csrfToken');
  const isAuthenticated = Reflect.get(value, 'isAuthenticated');
  const rawRegion = Reflect.get(value, 'consentRegion');
  if (typeof csrfToken !== 'string' || typeof isAuthenticated !== 'boolean') return undefined;

  let consentRegion: CookieConsentRegion | undefined;
  if (typeof rawRegion === 'object' && rawRegion !== null && !Array.isArray(rawRegion)) {
    const countryCode = Reflect.get(rawRegion, 'countryCode');
    const subdivisionCode = Reflect.get(rawRegion, 'subdivisionCode');
    if (
      typeof countryCode === 'string' &&
      /^[A-Z]{2}$/.test(countryCode) &&
      (subdivisionCode === undefined ||
        (typeof subdivisionCode === 'string' && /^[A-Z0-9]{1,3}$/.test(subdivisionCode)))
    ) {
      consentRegion = { countryCode, ...(subdivisionCode && { subdivisionCode }) };
    }
  }

  return { csrfToken, isAuthenticated, ...(consentRegion && { consentRegion }) };
}

/**
 * Initialize app by calling the backend /api/initialize endpoint.
 * This handles:
 * - CSRF token (set as cookie by backend)
 * - Device token (set as httpOnly cookie by backend)
 * - Session restoration if valid tokens exist
 *
 * Device token is now managed entirely via httpOnly cookies,
 * so we don't need to manually handle it in localStorage.
 *
 * @param apiUri - The API base URI (e.g., '/api' or 'http://localhost:4000')
 */
async function requestAppInitialization(apiUri: string): Promise<AppInitResult> {
  const initUrl = `${apiUri}/initialize`;
  try {
    const response = await fetch(initUrl, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });

    if (!response.ok) {
      const bodyText = await response.text().catch(() => undefined);
      const kind = classifyHttpStatus(response.status, bodyText);
      if (kind === SessionErrorKind.Transient) {
        await initializeCookieConsent();
        return { status: 'transient-failure' };
      }

      // Permanent 4xx (role mismatch, forbidden, etc.) — stop retrying so guards
      // can redirect or show the appropriate authenticated/unauthenticated state.
      await initializeCookieConsent();
      return { status: 'terminal-failure' };
    }

    const payload: unknown = await response.json();
    const data = readInitializeResponse(payload);
    if (!data) {
      await initializeCookieConsent();
      return { status: 'terminal-failure' };
    }
    await initializeCookieConsent(data.consentRegion);
    if (data.isAuthenticated) {
      return { status: 'ok', data };
    }

    return { status: 'unauthenticated', data };
  } catch {
    await initializeCookieConsent();
    return { status: 'transient-failure' };
  }
}

export function initializeApp(apiUri: string): Promise<AppInitResult> {
  const existingRequest = initializationRequests.get(apiUri);
  if (existingRequest) return existingRequest;

  const request = requestAppInitialization(apiUri).finally(() => initializationRequests.delete(apiUri));
  initializationRequests.set(apiUri, request);
  return request;
}
